import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Package, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { formatAED } from "@/data/products";

const inputCls =
  "w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm text-brandink outline-none transition-colors duration-200 placeholder:text-muted/70 focus:border-link";

const fmtError = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

export default function AccountPage() {
  const { customer, login, register, logout, authHeaders } = useAuth();
  const { t } = useLanguage();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (customer) {
      api.get("/my/orders", authHeaders()).then((d) => setOrders(d.orders)).catch(() => setOrders([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password, form.phone);
    } catch (err) {
      setError(fmtError(err));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-page px-4 py-10 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/"
          data-testid="account-back-home"
          className="mb-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-link transition-colors hover:text-brandink"
        >
          <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /> cambroo
        </Link>

        {customer === undefined ? (
          <div className="flex justify-center py-32">
            <Loader2 className="h-8 w-8 animate-spin text-link" />
          </div>
        ) : !customer ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mx-auto max-w-md rounded-xl border border-line bg-white p-8 shadow-sm"
            data-testid="account-auth-card"
          >
            <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-md bg-brand text-xl font-extrabold lowercase text-brandink">c</span>
            <h1 className="text-2xl font-extrabold text-brandink">
              {mode === "login" ? t("acc_login") : t("acc_register")}
            </h1>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="account-auth-form">
              {mode === "register" && (
                <>
                  <input data-testid="account-name-input" className={inputCls} placeholder={t("acc_name")} value={form.name} onChange={set("name")} />
                  <input data-testid="account-phone-input" className={inputCls} placeholder={t("acc_phone")} value={form.phone} onChange={set("phone")} />
                </>
              )}
              <input data-testid="account-email-input" className={inputCls} type="email" placeholder={t("acc_email")} value={form.email} onChange={set("email")} />
              <input data-testid="account-password-input" className={inputCls} type="password" placeholder={t("acc_password")} value={form.password} onChange={set("password")} />
              {error && <p data-testid="account-auth-error" className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-600">{error}</p>}
              <button
                data-testid="account-submit-button"
                type="submit"
                disabled={busy || !form.email || !form.password || (mode === "register" && !form.name)}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-brand py-3 text-sm font-bold text-brandink transition-colors duration-200 hover:bg-branddark disabled:opacity-40"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? t("acc_login") : t("acc_register")}
              </button>
            </form>
            <button
              data-testid="account-toggle-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="mt-5 text-xs font-semibold text-link transition-colors hover:underline"
            >
              {mode === "login" ? t("acc_no_account") : t("acc_have_account")}
            </button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid="account-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-line bg-white p-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">{t("acc_welcome")}</p>
                <h1 className="mt-1 text-2xl font-extrabold text-brandink" data-testid="account-customer-name">{customer.name}</h1>
                <p className="mt-1 text-sm text-muted">{customer.email}{customer.phone ? ` · ${customer.phone}` : ""}</p>
              </div>
              <button
                data-testid="account-logout-button"
                onClick={logout}
                className="flex items-center gap-2 rounded-md border border-line px-4 py-2.5 text-xs font-semibold text-brandink transition-colors hover:bg-page"
              >
                <LogOut className="h-3.5 w-3.5" /> {t("acc_logout")}
              </button>
            </div>

            <h2 className="mb-4 mt-8 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-brandink">
              <Package className="h-4 w-4 text-link" /> {t("acc_my_orders")}
            </h2>
            {orders === null ? (
              <Loader2 className="h-6 w-6 animate-spin text-link" />
            ) : orders.length === 0 ? (
              <p data-testid="account-no-orders" className="rounded-lg border border-line bg-white p-10 text-center text-sm text-muted">
                {t("acc_no_orders")}
              </p>
            ) : (
              <div className="space-y-3" data-testid="account-orders-list">
                {orders.map((o) => (
                  <div key={o.order_id} data-testid={`account-order-${o.order_id}`} className="rounded-lg border border-line bg-white p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span className="font-mono text-sm font-bold tracking-wider text-brandink">{o.order_id}</span>
                      <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${
                        o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-page text-muted"
                      }`}>
                        {o.payment_status === "paid" ? "Paid" : o.payment_method === "cod" ? "Cash on Delivery" : o.payment_method === "bank_transfer" ? "Bank Transfer" : o.payment_status}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-brandink/90">{o.items?.map((i) => `${i.name} ×${i.qty}`).join(", ")}</p>
                    <div className="mt-3 flex items-center justify-between text-xs text-muted">
                      <span>{new Date(o.created_at).toLocaleDateString("en-GB")} · {o.status}</span>
                      <span className="text-base font-extrabold text-brandink">{formatAED(o.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
